import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowLeft, RotateCcw, Clock, UtensilsCrossed } from 'lucide-react';
import Button from '../../components/ui/Button';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import EmptyState from './components/EmptyState';
import OrderHistoryList from './components/OrderHistoryList';
import DateFilterBar from './components/DateFilterBar';
import ReorderDialog from './components/ReorderDialog';
import { useOrderContext } from '../../context/OrderContext';
import { useRestaurantContext } from '../../context/RestaurantContext';

const OrderHistory = () => {
  const navigate = useNavigate();
  const { 
    orders, 
    isLoading, 
    isMoreLoading, 
    error, 
    pagination, 
    searchQuery,
    dateFilter,
    loadMore, 
    searchOrders,
    setDateFilter,
    resetToRecent
  } = useOrderContext();

  const { theme } = useRestaurantContext();
  const accentColor = theme?.primary || '#ff385c';

  const [expandedOrders, setExpandedOrders] = useState([]);
  const [isReorderDialogOpen, setIsReorderDialogOpen] = useState(false);
  const [selectedOrderForReorder, setSelectedOrderForReorder] = useState(null);
  
  // Debounced Search state
  const [searchTerm, setSearchTerm] = useState(searchQuery || '');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== searchQuery) {
        searchOrders(searchTerm);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm, searchOrders, searchQuery]);

  useEffect(() => {
    setSearchTerm(searchQuery || '');
  }, [searchQuery]);

  // Infinite Scroll Sentinel Observer
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && pagination.hasMore && !isMoreLoading && !isLoading) {
          loadMore();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [pagination.hasMore, isMoreLoading, isLoading, loadMore]);

  const handleToggleExpand = (orderId) => {
    setExpandedOrders(prev => 
      prev?.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleReorder = (order) => {
    setSelectedOrderForReorder(order);
    setIsReorderDialogOpen(true);
  };

  const handleConfirmReorder = (reorderData) => {
    navigate('/shopping-cart');
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setDateFilter(null);
    resetToRecent();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilters = Boolean(searchQuery || dateFilter);

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* ─── Sticky Header Bar with Search & Date Filters ─────────────────────── */}
      <header className="bg-white sticky top-0 z-30 shadow-[0_1px_3px_rgba(0,0,0,0.05)] border-b border-gray-100">
        <div className="max-w-xl mx-auto px-4 pt-3 pb-2.5 space-y-2.5">
          {/* Top Row: Back button, Title & Reset */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button 
                onClick={() => navigate('../', { relative: 'path' })} 
                className="w-9 h-9 -ml-1 rounded-full bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-gray-700 transition-colors active:scale-95 cursor-pointer"
                title="Go Back"
              >
                <ArrowLeft size={18} />
              </button>
              <div>
                <h1 className="text-base font-black text-gray-900 tracking-tight leading-none">
                  Orders & Bills
                </h1>
                <p className="text-[11px] text-gray-400 font-semibold mt-0.5">
                  {pagination.totalCount ? `${pagination.totalCount} total recorded` : 'Your past & active orders'}
                </p>
              </div>
            </div>

            {hasActiveFilters && (
              <button
                onClick={handleClearAllFilters}
                className="flex items-center gap-1 text-[11px] font-bold text-gray-500 hover:text-gray-900 px-2.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors active:scale-95"
              >
                <RotateCcw size={12} />
                <span>Reset</span>
              </button>
            )}
          </div>

          {/* Search Input Bar */}
          <div className="relative group">
            <Search 
              size={16} 
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-gray-900 transition-colors pointer-events-none" 
            />
            <input 
              type="text"
              placeholder="Search by #S-5, full order ID, or Bill #..."
              className="w-full pl-9 pr-9 py-2 bg-gray-100/80 border border-transparent rounded-xl text-xs font-semibold placeholder:text-gray-400 placeholder:font-normal focus:outline-none focus:bg-white focus:border-gray-300 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 transition-colors cursor-pointer"
                title="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>

          {/* Date Filter Bar */}
          <DateFilterBar 
            activeFilter={dateFilter}
            onSelectFilter={setDateFilter}
            accentColor={accentColor}
          />
        </div>
      </header>

      {/* ─── Main Content Body ────────────────────────────────────────────────── */}
      <main className="max-w-xl mx-auto p-3.5 space-y-3">
        {/* Active Query Subtitle */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between px-1 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
            <span>
              {searchQuery ? `Matching "${searchQuery}"` : 'Filtered Orders'}
              {pagination.totalCount !== undefined && ` (${pagination.totalCount} found)`}
            </span>
            {searchQuery && (
              <button
                onClick={() => setSearchTerm('')}
                className="text-gray-500 hover:text-gray-900 font-semibold normal-case underline underline-offset-2"
              >
                Clear Search
              </button>
            )}
          </div>
        )}

        {/* Initial Loading Spinner */}
        {isLoading && orders.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center text-gray-400 space-y-2">
            <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-900 animate-spin" />
            <p className="text-xs font-bold">Loading orders...</p>
          </div>
        ) : error && orders.length === 0 ? (
          <div className="bg-white rounded-2xl p-6 border border-red-100 text-center space-y-3 shadow-sm">
            <p className="text-xs font-bold text-red-600">Failed to load order history</p>
            <p className="text-[11px] text-gray-500">{error}</p>
            <Button onClick={handleClearAllFilters} variant="outline" size="sm">
              Try Again
            </Button>
          </div>
        ) : orders.length === 0 ? (
          <EmptyState
            hasFilters={hasActiveFilters}
            isHistoryMode={true}
            onLoadHistory={() => {}}
            onClearFilters={handleClearAllFilters}
          />
        ) : (
          <div className="space-y-4">
            <OrderHistoryList
              orders={orders}
              expandedOrders={expandedOrders}
              onToggleExpand={handleToggleExpand}
              onReorder={handleReorder}
              onViewDetails={() => {}}
              onGetHelp={() => {}}
            />

            {/* Infinite Scroll Loading Sentinel / Status */}
            <div ref={sentinelRef} className="pt-2 text-center">
              {isMoreLoading && (
                <div className="py-4 flex items-center justify-center gap-2 text-xs font-bold text-gray-500">
                  <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-900 animate-spin" />
                  <span>Loading more orders...</span>
                </div>
              )}

              {!pagination.hasMore && orders.length > 0 && (
                <div className="py-6 text-center">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 px-3 py-1 rounded-full bg-gray-100">
                    End of order records ({orders.length})
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      <BottomTabNavigation />

      {/* Reorder Dialog */}
      <ReorderDialog
        isOpen={isReorderDialogOpen}
        order={selectedOrderForReorder}
        onClose={() => {
          setIsReorderDialogOpen(false);
          setSelectedOrderForReorder(null);
        }}
        onConfirmReorder={handleConfirmReorder}
      />
    </div>
  );
};

export default OrderHistory;